/**
 * CampaignSummary - Total signups summary with campaign breakdown badges
 * Shows ALL campaigns (even those with 0 signups)
 */
import { Users, TrendingUp, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getThemeByKey, getCampaignTheme } from './campaignUtils';

export default function CampaignSummary({ 
  report, 
  allCampaigns, 
  onViewAllSignups 
}) {
  // Build merged campaign list: all campaigns with their signup counts
  // This ensures campaigns with 0 signups are still shown
  const mergedCampaigns = (allCampaigns || []).map(campaign => {
    const reportData = report?.campaigns?.find(c => c.campaign_id === campaign.campaign_id);
    return {
      campaign_id: campaign.campaign_id,
      title: campaign.title,
      theme: campaign.theme || reportData?.theme,
      signups: reportData?.signups || 0
    };
  }).sort((a, b) => a.campaign_id - b.campaign_id);

  return (
    <div 
      className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent rounded-2xl p-6 mb-8 border border-primary/20 dark:border-primary/30" 
      data-testid="total-signups-summary"
    >
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/20 dark:bg-primary/30 flex items-center justify-center">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <div>
            <p className="text-5xl font-bold text-foreground" data-testid="total-signups-count">
              {report?.total_signups || 0}
            </p>
            <p className="text-lg text-muted-foreground font-medium">Total Signups Across All Campaigns</p>
          </div>
        </div>
        
        {/* Campaign breakdown badges - ALL campaigns shown */}
        <div className="flex flex-wrap gap-3">
          {mergedCampaigns.map((campaign) => {
            const theme = getCampaignTheme(campaign.campaign_id, campaign.theme);
            return (
              <div 
                key={campaign.campaign_id}
                className={`px-4 py-2 rounded-xl ${theme.bg} ${theme.border} border flex items-center gap-2`}
                data-testid={`campaign-${campaign.campaign_id}-badge`}
              >
                <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${theme.gradient}`}></div>
                <span className="text-sm font-medium text-foreground">
                  C{campaign.campaign_id}: <span className={theme.color}>{campaign.signups}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Leading campaign and View All button */}
      <div className="mt-4 pt-4 border-t border-primary/20 dark:border-primary/30 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {report?.leading_campaign ? (
            <>
              <TrendingUp className="w-4 h-4 inline mr-1 text-green-500" />
              Campaign {report.leading_campaign} is leading with {report?.campaigns?.find(c => c.campaign_id === report.leading_campaign)?.signups || 0} signups
            </>
          ) : (
            'Start sharing QR codes to collect signups'
          )}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => onViewAllSignups(1)}
          data-testid="view-all-signups-summary-btn"
        >
          <List className="w-4 h-4 mr-1" />
          View All Signups
        </Button>
      </div>
    </div>
  );
}
